import { useState, useEffect, useCallback } from 'react';
import { useRouter } from '@framework/router';
import Flex from '@creditflowresearch/uilib/Flex';
import { useBreadcrumbs, useMounted } from '@/creditflowresearch/ui/common/hooks';
import DealTileWithFallback from '@/creditflowresearch/ui/components/deals/DealTiles/DealTileWithFallback';
import TopCompanies from '@/creditflowresearch/ui/components/TopCompanies/TopCompaniesCorporate';
import CorporateMarketChart from '@/creditflowresearch/ui/components/CorporateMarketChart';
import { ProfileTypes } from '@/creditflowresearch/application/actions/profile/constants';
import MissingSomethingModalButton from '@/creditflowresearch/ui/common/components/MissingSomethingModalButton';
import { usePageFiltersContext } from '@/creditflowresearch/application/PageFilters/PageFiltersContext';
import MetaSchema from '@/creditflowresearch/ui/common/components/MetaSchema';
import { ArticlesTilesWithFallback } from '@/creditflowresearch/ui/components/creditFlow/articles/ArticlesTiles';
import StatsTileWithFallback from '@/creditflowresearch/ui/components/creditFlow/Stats/StatsTile/StatsTileWithFallback';
import getProfileOverviewSchema from './getProfileMetaSchema';
import getProfileBreadcrumbs from './getProfileBreadcrumbs';
import useHeaderMenuData from '@/creditflowresearch/application/HeaderMenu/useHeaderMenuData';
import { useUserPlanContext } from '@/creditflowresearch/application/User/UserPlan/UserPlanContext';
import { UserPlanType } from '@creditflowresearch/user-plan/UserPlan';
import ProductPageHeader from '@/creditflowresearch/ui/common/components/templates/ProductPageHeader';
import { RoadshowsTileWithFallback } from '@/creditflowresearch/ui/components/Roadshows/Tile';
import { useCorporateMarketFilters } from '@/creditflowresearch/application/PageFilters/hooks';
import CorporateDealByMarketTile from '@/creditflowresearch/ui/components/deals/DealTiles/CorporateDealByMarketTile';
import { useProductGradient } from '@/creditflowresearch/ui/common/hooks/products/useProductGradient';
import { useSetMetaTags } from '@/creditflowresearch/application/actions/profile/hooks/useSetMetaTags';
import { useFeatureToggleContext } from '@framework/ui/Organisms/FeatureToggle/Context';
import { HIDE_TOP_COMPANIES } from '@creditflowresearch/config/featureToggle';
import { useCorporateMarketFilterToggle } from '@/creditflowresearch/ui/common/hooks/filters/useCorporateMarketFilterToggle';
import { useAccessGrant } from '@/creditflowresearch/application/User/access/grants/useAccessGrant';

import styles from './profile.module.scss';

const CorporateMarketProfile = () => {
  const { isHeaderInitialized } = useHeaderMenuData();
  const [pageHeader, setPageHeader] = useState<string>('');
  const { query, asPath } = useRouter();
  const {
    pageFilters,
    checkAccessByUserMarket,
    checkAccessByUserProducts,
  } = usePageFiltersContext();
  const isMounted = useMounted();
  const setMetaTags = useSetMetaTags();
  const { getFeatureByName } = useFeatureToggleContext();
  const { isEnabled: showTopCompanies } = getFeatureByName(HIDE_TOP_COMPANIES);
  const granted = useAccessGrant();

  const { checkAccessByUserPlan } = useUserPlanContext();
  const { handleFilterToggle } = useCorporateMarketFilterToggle();

  const onToggle = useCallback((searchQuery: string) => {
    handleFilterToggle(searchQuery);
  }, [handleFilterToggle]);

  const userAccessChecks = [
    checkAccessByUserMarket(),
    checkAccessByUserProducts(),
  ];

  const updatePageHeaderAndTileText = (pageHeader: string = 'Market Overview') => {
    setPageHeader(pageHeader);
  };

  const getBreadcrumbs = () => {
    return getProfileBreadcrumbs({
      profileType: ProfileTypes.OVERVIEW,
    }, query);
  };

  const getSchema = () => {
    return getProfileOverviewSchema(asPath);
  };

  useBreadcrumbs(
    () => getBreadcrumbs(),
    [isHeaderInitialized, pageFilters, query, ProfileTypes.OVERVIEW],
  );

  useEffect(() => {
    if (!isMounted) {
      return;
    }

    updatePageHeaderAndTileText();
  }, [isHeaderInitialized, query, isMounted]);

  useEffect(() => {
    updatePageHeaderAndTileText();
  }, [isHeaderInitialized, pageFilters]);

  useEffect(() => {
    if (isHeaderInitialized) {
      setMetaTags({
        pathname: asPath,
      });
    }
  }, [isHeaderInitialized, asPath, setMetaTags]);

  return (
    <>
      <MetaSchema schema={ getSchema() } />
      <div className={ styles.profileLayout }>
        <ProductPageHeader
          title={ pageHeader }
          panelBackground={ useProductGradient() }
          items={ useCorporateMarketFilters() }
          onToggle={ onToggle }
          accessConditions={ [{ checks: [granted || checkAccessByUserPlan(UserPlanType.STARTER)] }] }
          blockingAccessConditions={ [{ checks: [granted || checkAccessByUserPlan(UserPlanType.STARTER)] }] }
        />

        <ArticlesTilesWithFallback userAccessChecks={ userAccessChecks } />
        <RoadshowsTileWithFallback userAccessChecks={ userAccessChecks } />
        <DealTileWithFallback userAccessChecks={ userAccessChecks }>
          <CorporateDealByMarketTile />
        </DealTileWithFallback>
        { showTopCompanies ? (
          <Flex direction={ { sm: 'column', md: 'column', xl: 'row' } }>
            <TopCompanies className={ styles.topParentsWrapper } />
            <CorporateMarketChart className={ styles.chartWrapper } />
          </Flex>
        ) : (
          <CorporateMarketChart className={ styles.chartWrapperFullWidth } />
        ) }
        <StatsTileWithFallback userAccessChecks={ userAccessChecks } />
      </div>
      <MissingSomethingModalButton />
    </>
  );
};

export default CorporateMarketProfile;
